import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useAuth } from '@/lib/auth'
import { exchangeSsoCodeApi } from '@/lib/api'
import { toast } from 'sonner'
import { LoaderIcon, ShieldCheckIcon } from 'lucide-react'
import { BRAND_MINT } from '@/lib/utils'

/**
 * SSO callback route — receives a short-lived, single-use exchange code
 * from the API after successful SAML/OIDC authentication. The code is
 * exchanged over HTTPS before the URL is removed from browser history.
 *
 * URL: /sso-callback?code=...
 */
export const Route = createFileRoute('/sso-callback')({
  component: () => {
    const navigate = useNavigate()
    const { login } = useAuth()
    const processedRef = useRef(false)

    useEffect(() => {
      // Guard against React strict mode double-mount
      if (processedRef.current) return
      processedRef.current = true

      const params = new URLSearchParams(window.location.search)

      const code = params.get('code')
      if (!code) {
        toast.error('SSO login failed. No authentication code received.')
        navigate({ to: '/login', search: { error: 'sso_failed' } as any })
        return
      }

      // Strip the one-time code from URL immediately.
      window.history.replaceState({}, '', '/sso-callback')

      // Exchange the code for tokens over HTTPS; bearer tokens never enter
      // the URL or browser history.
      exchangeSsoCodeApi(code)
        .then((data) => login(data.token, data))
        .then(() => {
          toast.success('Signed in successfully!')
          navigate({ to: '/dashboard' })
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : 'Failed to complete SSO login'
          toast.error(message)
          navigate({ to: '/login', search: { error: 'sso_failed' } as any })
        })

      return

      // The code is consumed above; no token-bearing URL data is trusted.
      /*

      */
    }, [])

    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-[#0B1120]">
        <div className="relative flex flex-col items-center gap-6 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-12 shadow-2xl shadow-black/50">
          <span
            aria-hidden="true"
            className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
            style={{ backgroundColor: BRAND_MINT }}
          />

          <div className="flex size-14 items-center justify-center rounded-full bg-[#00E5A0]/10">
            <ShieldCheckIcon className="size-7" style={{ color: BRAND_MINT }} />
          </div>

          <h1 className="text-xl font-semibold text-white">Completing sign-in</h1>
          <p className="text-sm text-white/50">Verifying your identity and setting up your session…</p>

          <LoaderIcon className="size-5 animate-spin" style={{ color: BRAND_MINT }} />
        </div>
      </div>
    )
  },
})
